import { Button, H2, H5 } from "@sellar-io/components";
import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { ReactElement, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../../../components";
import {
  CHECKOUT_EDIT_ORDER_BUYER,
  CHECKOUT_BUYER,
  ORDER_BUYER,
} from "../../../../constants/routes";
import db from "../../../../db";
import {
  BasketContextActionEnum,
  useBasketContext,
  useEvents,
} from "../../../../hooks";
import { useStorefrontLink } from "../../../../hooks/api/v2/storefrontLink/useStorefrontLink";
import { useBuyerRelationshipList } from "../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useSignupContext } from "../../../../hooks/useSignupContext.shared";

import { deleteUnauthenticatedDraftBasket } from "../deleteUnauthenticatedDraftBasket";
import { updateDraftOrderBuyerId } from "../useUpdateDraftOrderBuyerId";

export function UserSignupConfirmationStepContent({
  supplier,
}: {
  supplier?: SupplierGetResponseType;
}): ReactElement {
  const { track } = useEvents();
  const { endOnboarding, basketTotal, company } = useSignupContext();
  const [searchParams] = useSearchParams();
  const { business } = useSellarCurrentBusiness();
  const [, dispatch] = useBasketContext();
  const navigate = useNavigate();

  const hasBasketTotal = !!(basketTotal || basketTotal === 0);
  const [priceTierId, setPriceTierId] = useState<number>();
  const [loadedDraftOrderSupplierId, setLoadedDraftOrderSupplierId] =
    useState<number>();
  const connectionQueryEnabled = !!business?.Retailer?.id && hasBasketTotal;
  const buyerRelationshipQuery = useBuyerRelationshipList(
    {
      query: [
        {
          type: "supplierId",
          modifier: "EQUALS",
          value: supplier?.id ?? 0,
        },
      ],
      limit: 1,
    },
    {
      enabled: connectionQueryEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const storefrontLinkPassword = searchParams.get("storefrontLink");
  const orderFlowToken = searchParams.get("orderFlowToken");
  const storefrontLinkQueryEnabled =
    !!storefrontLinkPassword && !!supplier?.id && !orderFlowToken;

  const storefrontLinkQuery = useStorefrontLink(
    storefrontLinkPassword,
    {
      SupplierId: supplier?.id ?? 0,
    },
    {
      enabled: storefrontLinkQueryEnabled,
    },
  );

  const isStorefrontLinkLoading =
    storefrontLinkQuery.isPending && storefrontLinkQueryEnabled;
  const storefrontLink = storefrontLinkQuery.data;
  const connection =
    buyerRelationshipQuery.data?.pages[0]?.[0]?.Relationship
      .BuyerRelationshipDetails;
  const isConnectionLoading =
    buyerRelationshipQuery.isLoading && connectionQueryEnabled;

  useEffect(() => {
    if (!supplier?.id) return;
    if (!orderFlowToken) {
      void db.draftOrders
        .get({
          RetailerId: 0,
          SupplierId: supplier.id,
          initiator: "retailer",
        })
        .then((order) => {
          const draftPriceTierId =
            order?.OrderItems?.[0]?.Product.ProductPriceTier?.PriceTierId;
          setPriceTierId(draftPriceTierId ?? undefined);
          setLoadedDraftOrderSupplierId(supplier.id);
        });
    }
  }, [supplier?.id, orderFlowToken]);

  const loading = Boolean(
    supplier?.id &&
    !orderFlowToken &&
    loadedDraftOrderSupplierId !== supplier.id,
  );

  const hasBasketSharedEvent = async () => {
    if (supplier?.id) {
      // Existing storefront link flow: update the draft order's buyer ID
      await updateDraftOrderBuyerId({
        supplierId: supplier.id,
        newBuyerId: business?.Retailer?.id ?? 0,
        dispatch,
      });
    }
    // For orderFlowToken flow: basket is already restored by recovery effect
    // No action needed here
    endOnboarding();
  };

  const hasConnectionId = !!connection;
  const connectionPriceTierNotMatched =
    priceTierId !== connection?.PriceTier?.id;
  const hasStorefrontLinkConnectionId =
    !!storefrontLink?.SupplierRetailerConnection.id;
  const hasStorefrontLinkConnectionAndConnection =
    hasConnectionId && hasStorefrontLinkConnectionId;
  const storefrontLinkConnectionNotMatched =
    hasStorefrontLinkConnectionAndConnection &&
    storefrontLink.SupplierRetailerConnection.id !== connection.id;

  if (orderFlowToken) {
    return (
      <>
        <div className={"flex flex-col gap-4"}>
          <H2 className={"text-center"}>
            Thanks for setting up your Sellar account!
          </H2>
          <p className={"text-center"}>
            You can now order from the best craft drinks producers in the UK,
            all in one place.
          </p>
          <div
            className={
              "flex flex-col items-center gap-2 rounded-sm bg-neutral-100 p-6"
            }
          >
            <div className="flex flex-col">
              <ProfilePicture
                name={business?.name}
                logoUrl={business?.logoUrl ?? ""}
              />
              <Icon
                size={0.65}
                icon={"CHECK_CIRCLE"}
                className={
                  "flex-end -mt-4 ml-auto rounded-full bg-white text-green-500"
                }
              />
            </div>

            <div className="text-center">
              <H5>{business?.name}</H5>
              <H5 className="text-zinc-500">{company?.companyName}</H5>
            </div>
          </div>
        </div>
        {hasBasketTotal && supplier?.id && business?.Retailer?.id ? (
          <div className={"flex flex-col gap-2 lg:flex-row"}>
            <Button
              color={"secondary"}
              className={"w-full"}
              onClick={async () => {
                endOnboarding();
              }}
            >
              Continue ordering
            </Button>
            <Button
              className={"w-full"}
              onClick={async () => {
                // For orderFlowToken flow, load the saved draft order and dispatch to basket
                // This ensures the checkout page sees the modified order items

                const savedOrder = await db.draftOrders
                  .where({
                    initiator: "retailer",
                    RetailerId: business.Retailer?.id ?? 0,
                  })
                  .first();

                if (savedOrder?.id && savedOrder._fromOrderFlowToken) {
                  dispatch({
                    type: BasketContextActionEnum.CHANGE_ORDER,
                    order: savedOrder,
                  });

                  track("Checkout Started", {
                    retailerName: business.name,
                    supplierName: supplier.Business.name,
                    retailerId: business.Retailer?.id,
                    supplierId: supplier.id,
                  });

                  endOnboarding();

                  return navigate(
                    CHECKOUT_EDIT_ORDER_BUYER.href({
                      supplierIdOrUrlId: supplier.urlId,
                      orderId: savedOrder.id,
                    }),
                  );
                }
              }}
            >
              Go to checkout
            </Button>
          </div>
        ) : (
          <Button
            className={"w-full"}
            onClick={async () => {
              endOnboarding();
            }}
          >
            Finish
          </Button>
        )}
      </>
    );
  }

  return (
    <>
      {isConnectionLoading || loading || isStorefrontLinkLoading ? (
        <SpinnerLegacy />
      ) : (
        <div className={"m-auto flex h-full flex-col justify-center gap-4"}>
          {hasConnectionId &&
          (connectionPriceTierNotMatched ||
            storefrontLinkConnectionNotMatched) ? (
            //   if the buyer has a connection with the supplier & the price tier on the order does not match price tier on connection
            // OR has a connection, and the connection ID does not match the storefront link connectionId then default to this screen
            <>
              <div className={"flex flex-col gap-2"}>
                <H2 className={"text-center"}>
                  You already have a connection with {supplier?.Business.name}{" "}
                  on Sellar
                </H2>
                <p>
                  This means that the pricing for products currently in your
                  basket may be different to the pricing assigned to you by{" "}
                  {supplier?.Business.name}.
                </p>
              </div>
              <StartOverNewPricingButton supplier={supplier} />
            </>
          ) : (
            <>
              {/*No connection with supplier, but the storefront link has a connection ID, default to incorrect pricing*/}
              {hasBasketTotal &&
              hasStorefrontLinkConnectionId &&
              !hasConnectionId ? (
                <StorefrontLinkConnectionIncorrectPricing supplier={supplier} />
              ) : (
                // No connection, no storefront link connection, default to continue screen
                <>
                  <div className={"flex flex-col gap-4"}>
                    <H2 className={"text-center"}>
                      Thanks for setting up your Sellar account!
                    </H2>
                    <p className={"text-center"}>
                      You can now order from the best craft drinks producers in
                      the UK, all in one place.
                    </p>
                    <div
                      className={
                        "flex flex-col items-center gap-2 rounded-sm bg-neutral-100 p-6"
                      }
                    >
                      <div className="flex flex-col">
                        <ProfilePicture
                          name={business?.name}
                          logoUrl={business?.logoUrl ?? ""}
                        />
                        <Icon
                          size={0.65}
                          icon={"CHECK_CIRCLE"}
                          className={
                            "flex-end -mt-4 ml-auto rounded-full bg-white text-green-500"
                          }
                        />
                      </div>

                      <div className="text-center">
                        <H5>{business?.name}</H5>
                        <H5 className="text-zinc-500">
                          {company?.companyName}
                        </H5>
                      </div>
                    </div>
                  </div>
                  {hasBasketTotal && supplier?.id && business?.Retailer?.id ? (
                    <div className={"flex flex-col gap-2 lg:flex-row"}>
                      <Button
                        color={"secondary"}
                        className={"w-full"}
                        onClick={async () => {
                          await hasBasketSharedEvent();
                        }}
                      >
                        Continue ordering
                      </Button>
                      <Button
                        className={"w-full"}
                        onClick={async () => {
                          await hasBasketSharedEvent();
                          // Temp fix until we refactor top level loading states
                          // Business is taking too long to laod and update basket context
                          setTimeout(() => {
                            track("Checkout Started", {
                              retailerName: business.name,
                              supplierName: supplier.Business.name,
                              retailerId: business.Retailer?.id,
                              supplierId: supplier.id,
                            });
                            return navigate(
                              `${CHECKOUT_BUYER.href({
                                supplierIdOrUrlId: supplier.urlId,
                              })}${location.search}`,
                            );
                          }, 1000);
                        }}
                      >
                        Go to checkout
                      </Button>
                    </div>
                  ) : (
                    <Button
                      className={"w-full"}
                      onClick={async () => {
                        endOnboarding();
                      }}
                    >
                      Finish
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

const StartOverNewPricingButton = ({
  supplier,
}: {
  supplier?: SupplierGetResponseType;
}) => {
  const navigate = useNavigate();
  const { endOnboarding } = useSignupContext();
  return (
    <Button
      className={"w-full"}
      onClick={async () => {
        if (supplier?.id) {
          await deleteUnauthenticatedDraftBasket({
            supplierId: supplier.id,
          });
          navigate(ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId }));
        }
        endOnboarding();
      }}
    >
      Start over with my pricing
    </Button>
  );
};
const StorefrontLinkConnectionIncorrectPricing = ({
  supplier,
}: {
  supplier?: SupplierGetResponseType;
}) => {
  return (
    <>
      <div className={"flex flex-col gap-2"}>
        <H2 className={"text-center"}>Incorrect pricing</H2>
        <p>
          The pricing you've been shown via this link may not be correct for
          your venue. You'll need to recreate your order with the correct
          pricing.
        </p>
      </div>
      <StartOverNewPricingButton supplier={supplier} />
    </>
  );
};
